import { ReactNode, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Building2,
  CheckCheck,
  ClipboardList,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Ship,
  Sun,
  Users,
  X,
  Globe,
  MessageCircleMore,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { NotifBell } from './NotifBell';
import type { UserRole } from '@/lib/types';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: UserRole[];
  children?: NavItem[];
}

const NAV: NavItem[] = [
  { to: '/', label: 'Mi trabajo', icon: ClipboardList, roles: ['agente', 'admin'] },
  { to: '/board', label: 'Mi board', icon: LayoutDashboard, roles: ['agente', 'admin', 'supervisor', 'ventas', 'gerente', 'director'] },
  {
    to: '/reportes',
    label: 'Reportes',
    icon: FileText,
    roles: ['supervisor', 'admin', 'ventas', 'director'],
    children: [
      { to: '/reportes/bookings', label: 'Bookings', icon: Ship, roles: ['supervisor', 'admin', 'ventas', 'director'] },
      { to: '/reportes/operaciones', label: 'Operaciones', icon: Ship, roles: ['supervisor', 'admin', 'ventas', 'agente', 'gerente', 'director'] },
      // TEMPORAL: staging de los Excel de los operativos, en validación
      { to: '/reportes/operaciones-excel', label: 'Operaciones (Excel)', icon: Ship, roles: ['supervisor', 'admin', 'agente', 'gerente', 'director'] },
      // Antes correo diario a Edwing; ahora página en vivo (solo admin)
      { to: '/reportes/cierres-pendientes', label: 'Cerrados con pendientes', icon: CheckCheck, roles: ['admin', 'director'] },
      { to: '/reportes/operativos', label: 'Tareas', icon: CheckCheck, roles: ['supervisor', 'admin', 'director'] },
    ],
  },
  { to: '/clientes', label: 'Clientes', icon: Building2, roles: ['cobranza', 'admin', 'supervisor', 'gerente', 'director'] },
  // Bitácora de gestiones de cobranza (Sheet de Fer → Atlas, 7-oct-2026)
  { to: '/comunicaciones-cxc', label: 'Comunicaciones CxC', icon: MessageCircleMore, roles: ['cobranza', 'admin', 'supervisor', 'director'] },
  { to: '/equipo', label: 'Equipo', icon: Users, roles: ['gerente', 'admin', 'supervisor', 'director'] },
  { to: '/admin', label: 'Admin', icon: Settings, roles: ['admin', 'gerente'] }, // gerente: solo Delegaciones
];

const linkClass =
  (collapsed: boolean) =>
  ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center rounded-md text-sm font-medium transition-colors',
      collapsed ? 'justify-center p-2' : 'gap-2 px-3 py-2',
      isActive
        ? 'bg-brand-50 text-brand-700 border-l-2 border-brand-500 -ml-0.5'
        : 'text-ink-700 hover:bg-surface-2',
    );

function NavItems({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { user } = useAuth();
  if (!user) return null;
  const canSee = (n: NavItem) => {
    // Clientes (CxC): de los gerentes, solo los de VENTAS lo ven
    if (n.to === '/clientes' && user.rol === 'gerente' && user.gerenteTipo !== 'ventas')
      return false;
    return (
      n.roles.includes(user.rol) ||
      (n.children?.some((c) => c.roles.includes(user.rol)) ?? false)
    );
  };
  const items = NAV.filter(canSee);
  return (
    <div className="p-2 space-y-0.5">
      {items.map((item) => {
        const { to, label, icon: Icon, children } = item;
        if (children?.length) {
          const kids = children.filter((c) => c.roles.includes(user.rol));
          // Plegado: los subitems se muestran como iconos (con tooltip).
          if (collapsed) {
            return (
              <div key={to} className="pt-1 space-y-0.5">
                {kids.map(({ to: kto, label: klabel, icon: KIcon }) => (
                  <NavLink key={kto} to={kto} onClick={onNavigate} title={klabel} className={linkClass(true)}>
                    <KIcon className="h-5 w-5 shrink-0" />
                  </NavLink>
                ))}
              </div>
            );
          }
          return (
            <div key={to} className="pt-1">
              <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-ink-400">
                <Icon className="h-4 w-4 shrink-0" />
                <span>{label}</span>
              </div>
              <div className="ml-4 border-l border-ink-100 pl-1 space-y-0.5">
                {kids.map(({ to: kto, label: klabel, icon: KIcon }) => (
                  <NavLink key={kto} to={kto} onClick={onNavigate} className={linkClass(false)}>
                    <KIcon className="h-4 w-4 shrink-0" />
                    <span>{klabel}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          );
        }
        return (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            onClick={onNavigate}
            title={collapsed ? label : undefined}
            className={linkClass(collapsed)}
          >
            <Icon className={collapsed ? 'h-5 w-5 shrink-0' : 'h-4 w-4 shrink-0'} />
            {!collapsed && <span>{label}</span>}
          </NavLink>
        );
      })}
    </div>
  );
}

function BrandHeader({ collapsed = false }: { collapsed?: boolean }) {
  if (collapsed) {
    return (
      <div className="p-4 border-b border-ink-200 flex justify-center">
        <Globe className="h-5 w-5 text-brand-500" />
      </div>
    );
  }
  return (
    <div className="p-4 border-b border-ink-200">
      <div className="font-bold text-lg leading-none flex items-center gap-1.5">
        <Globe className="h-5 w-5 text-brand-500" />
        <span className="text-ink-900">Atlas</span>
      </div>
      <div className="text-xs text-ink-400 mt-1">STRD logística</div>
    </div>
  );
}

/** Sidebar visible siempre en md+ (plegable). */
function DesktopSidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <nav
      className={cn(
        'hidden md:flex md:flex-col shrink-0 border-r border-ink-200 bg-surface-1 transition-[width] duration-150',
        collapsed ? 'w-16' : 'w-56',
      )}
    >
      <BrandHeader collapsed={collapsed} />
      <div className="flex-1 overflow-y-auto">
        <NavItems collapsed={collapsed} />
      </div>
      <button
        onClick={onToggle}
        className="m-2 p-2 rounded-md text-ink-400 hover:bg-surface-2 hover:text-ink-900 flex items-center justify-center"
        title={collapsed ? 'Expandir menú' : 'Plegar menú'}
        aria-label={collapsed ? 'Expandir menú' : 'Plegar menú'}
      >
        {collapsed ? (
          <PanelLeftOpen className="h-4 w-4" />
        ) : (
          <PanelLeftClose className="h-4 w-4" />
        )}
      </button>
    </nav>
  );
}

/** Drawer overlay para móvil */
function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Bloquear scroll del body cuando el drawer está abierto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity md:hidden',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel */}
      <nav
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-surface-1 border-r border-ink-200 transition-transform md:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Navegación principal"
      >
        <div className="flex items-center justify-between border-b border-ink-200">
          <BrandHeader />
          <button
            onClick={onClose}
            className="p-3 text-ink-400 hover:text-ink-900"
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <NavItems onNavigate={onClose} />
      </nav>
    </>
  );
}

/** Botón de modo claro/oscuro. Persiste en localStorage y togglea .dark en <html>. */
function ThemeToggle() {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains('dark'),
  );
  const toggle = () => {
    const next = !dark;
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
    setDark(next);
  };
  return (
    <button
      onClick={toggle}
      className="p-2 rounded-md text-ink-400 hover:bg-surface-2 hover:text-ink-900"
      aria-label={dark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      title={dark ? 'Modo claro' : 'Modo oscuro'}
    >
      {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

function TopBar({ onMenuClick }: { onMenuClick: () => void }) {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <header className="h-14 border-b border-ink-200 bg-surface-1 px-2 sm:px-4 flex items-center gap-2 sticky top-0 z-10">
      {/* Hamburger solo en móvil */}
      <button
        onClick={onMenuClick}
        className="md:hidden p-2 rounded-md text-ink-700 hover:bg-surface-2"
        aria-label="Abrir menú"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Brand pequeño en móvil — el sidebar no se ve */}
      <div className="md:hidden font-bold text-base flex items-center gap-1">
        <Globe className="h-4 w-4 text-brand-500" />
        <span className="text-ink-900">Atlas</span>
      </div>

      <div className="flex-1" />

      <NotifBell />
      <ThemeToggle />

      <div className="flex items-center gap-2 px-2 py-1 rounded-md">
        {user.avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={user.avatarUrl}
            alt={user.nombre}
            className="w-7 h-7 rounded-full"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-7 h-7 rounded-full bg-brand-500 text-white text-xs font-medium flex items-center justify-center">
            {user.nombre
              .split(' ')
              .map((s) => s[0])
              .slice(0, 2)
              .join('')
              .toUpperCase()}
          </div>
        )}
        <div className="hidden sm:flex flex-col leading-tight">
          <span className="text-sm font-medium">{user.nombre}</span>
          <span className="text-xs text-ink-400">{user.rol}</span>
        </div>
      </div>
      <button
        onClick={logout}
        className="p-2 rounded-md text-ink-400 hover:bg-surface-2 hover:text-ink-900"
        aria-label="Cerrar sesión"
        title="Cerrar sesión"
      >
        <LogOut className="h-4 w-4" />
      </button>
    </header>
  );
}

/** Banner fijo mientras se está en modo "Ver como" (solo staging): muestra la
 *  sesión suplantada y permite volver a la cuenta admin original. */
function VerComoBanner() {
  const { user } = useAuth();
  const original = localStorage.getItem('opsapp_token_original');
  if (!original) return null;
  const volver = () => {
    localStorage.setItem('opsapp_token', original);
    localStorage.removeItem('opsapp_token_original');
    window.location.href = '/admin';
  };
  return (
    <div className="bg-amber-500 text-white text-sm px-4 py-1.5 flex items-center justify-center gap-3 shrink-0">
      <span>
        👁 Viendo como <b>{user?.nombre}</b> ({user?.rol}) — sesión de prueba
      </span>
      <button
        onClick={volver}
        className="underline font-semibold hover:text-amber-100"
      >
        Volver a mi cuenta
      </button>
    </div>
  );
}

export function AppLayout({ children }: { children?: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === '1',
  );
  const location = useLocation();

  // Cerrar drawer al cambiar de ruta
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('sidebar-collapsed', next ? '1' : '0');
      return next;
    });

  return (
    <div className="flex h-screen overflow-hidden">
      <DesktopSidebar collapsed={collapsed} onToggle={toggleCollapsed} />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <VerComoBanner />
        <TopBar onMenuClick={() => setDrawerOpen(true)} />
        <main className="flex-1 overflow-auto bg-surface-1">{children ?? <Outlet />}</main>
      </div>
    </div>
  );
}
